import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import * as Auth from 'aws-amplify/auth';
import {
  Bell,
  ChevronDown,
  RefreshCw,
  Search,
  User,
} from "lucide-react";
import '../LandingPage.css';

/**
 * Shared header component that appears on all pages
 */
export default function AppHeader({ signOut, onRefresh, refreshing }) {
  const navigate = useNavigate();
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [userName, setUserName] = useState("User");

  useEffect(() => {
    fetchUserName();
  }, []);

  const fetchUserName = async () => {
    try {
      const userAttributes = await Auth.fetchUserAttributes();
      const name = userAttributes.name || userAttributes.email?.split('@')[0] || "User";
      setUserName(name);
    } catch (error) {
      console.error("Error fetching user name:", error);
    }
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      // Navigate to RunOneLongTerm with the search query as customList
      navigate('/runOneLongTerm', {
        state: {
          customList: searchQuery.trim(),
          autoRun: true
        }
      });
      setSearchQuery(''); // Clear search after navigating
    }
  };

  const handleRefresh = () => {
    if (onRefresh && !refreshing) {
      onRefresh();
    }
  };

  return (
    <header className="landing-header">
      <div className="landing-header-content">
        {/* Left: Market Intelligence Title & Nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <div
            onClick={() => navigate('/')}
            style={{
              fontSize: '18px',
              fontWeight: '700',
              color: 'rgba(0, 0, 0, 0.9)',
              letterSpacing: '-0.5px',
              cursor: 'pointer',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(0, 0, 0, 0.9)'}
          >
            Market Intelligence
          </div>

          <nav className="landing-nav">
            <button className="landing-nav-link" onClick={() => navigate('/marketDirection')}>
              Market Direction
            </button>
            <button className="landing-nav-link" onClick={() => navigate('/economicEvents')}>
              Economic Events
            </button>
            <button className="landing-nav-link" onClick={() => navigate('/aiAnalysis')}>
              AI Analysis
            </button>
            <button className="landing-nav-link" onClick={() => navigate('/reports')}>
              Reports
            </button>
          </nav>
        </div>

        {/* Right: Search, Status, User */}
        <div className="landing-header-right">
          {onRefresh && (
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              style={{
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '8px',
                padding: '8px 12px',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#10b981',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.2s',
                opacity: refreshing ? 0.6 : 1
              }}
              onMouseEnter={(e) => !refreshing && (e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)')}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)'}
              title="Refresh data"
            >
              <RefreshCw size={14} className={refreshing ? 'spinning' : ''} />
              <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          )}

          <div className="landing-search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search stocks, ETFs, indices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleSearch}
              style={{
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: 'rgba(0, 0, 0, 0.9)',
                fontSize: '14px',
                width: '200px'
              }}
            />
          </div>

          <button style={{
            background: 'rgba(0, 0, 0, 0.05)',
            border: '1px solid rgba(0, 0, 0, 0.2)',
            borderRadius: '8px',
            padding: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: 'black'
          }}>
            <Bell size={16} />
          </button>

          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setUserDropdownOpen(!userDropdownOpen)}
              style={{
                background: 'rgba(0, 0, 0, 0.05)',
                border: '1px solid rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                padding: '8px 12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: 'black',
                fontSize: '14px'
              }}
            >
              <User size={16} />
              <span>{userName}</span>
              <ChevronDown
                size={14}
                style={{
                  transition: 'transform 0.2s',
                  transform: userDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
              />
            </button>
            {userDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                background: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                minWidth: '160px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                zIndex: 1000
              }}>
                <button
                  onClick={() => {
                    setUserDropdownOpen(false);
                    navigate('/editProfile');
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(0, 0, 0, 0.9)',
                    fontSize: '14px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.05)'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  Edit Profile
                </button>
                <button
                  onClick={() => {
                    setUserDropdownOpen(false);
                    signOut();
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(0, 0, 0, 0.9)',
                    fontSize: '14px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.05)'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
